"use strict";

import Analytics from "/js/util/analytics.js";
import CardView from "/js/card-views/card-view.js";
import FadeUtil from "/js/util/fade-util.js";
import Global from "/js/core/global.js";
import PrimerMaster from "/js/util/primermaster.js";
import Constants from '/js/core/constants.js';
import FocusUtil from '/js/util/focus-util.js';

/**
 * Lesson tap-to-reveal card, which has 2-4 clickable elements that expand and contract.
 */
export default class TapRevealCardView extends CardView {
  
  static _TEMPLATE_STRING = `
      <div class="caption"><%= caption %></div>
      <div class="holder"></div>
      `;
  static _template = _.template(TapRevealCardView._TEMPLATE_STRING);

  constructor(data) {
    super(data);

    this._$holder = this.$el.find(".holder");
    this._items = [];

    data.list.forEach((o, i) => {
      const item = new TapRevealItem(o);
      this._items.push(item);
      this._$holder.append(item.$el);
      FocusUtil.setEnterHandler(item.$el, e => this.#onItemEnter(e));
      item.$el.on("click", e => FocusUtil.blurActiveElement());
    });

    this._selectedIndex = 0;
  }

  /* Override */
  makeContent() {
    const o = this.data;
    return TapRevealCardView._template(o);
  }

  /* Override */
  onFirstShow() {
    this._items.forEach(item => item.onFirstShow());
    // This will trigger a layout:
    this.setSelectedIndex(this._selectedIndex);  
  }

  /* Override */
  get isActive() {
    // When setter is overridden, getter must be as well (!!)
    return super.isActive;
  }

  /* Override */
  set isActive(b) {
    super.isActive = b;
    const tabIndex = b ? Constants.TABINDEX_CONTENT_GENERAL : -1;
    for (const item of this._items) {
      item.$el.attr("tabindex", tabIndex);
    }
  }

  setSelectedIndex(selectedIndex) {
    
    this._selectedIndex = selectedIndex;

    const holderHeight = this._$holder.height();
    const itemUnrevealedHeight = 60;

    if (selectedIndex === -1) {
      const itemHeight = holderHeight / this._items.length;
      this._items.forEach((item, i) => {
        item.setRevealed(false);
        const y = i * itemHeight;
        item.$el.css("top", y + "px");
        item.$el.height(itemHeight);
      });
    } else {
      let y = 0;
      this._items.forEach((item, i) => {
        const isSelected = (i === selectedIndex);
        item.setRevealed(isSelected);
        const itemHeight = isSelected 
          ? holderHeight - itemUnrevealedHeight * (this._items.length - 1)
          : itemUnrevealedHeight;
        item.$el.css("top", y + "px");
        item.$el.height(itemHeight);
        y += itemHeight;
      });
    }
  }

  /** Override */
  onDelegateClick(x, y, deltaX, deltaY, e) {
    const index = this.#getItemIndexUsingChild(e.target);
    if (index === -1) {
      // Item "subview" was not clicked, let base class handle.
      super.onDelegateClick(x, y, deltaX, deltaY, e);
      return;
    }
    this.#toggleItem(index);
  }

  /** Enter key was pressed on focused item. */
  #onItemEnter(e) {
    const index = this.#getItemIndexUsingChild(e.target);
    if (index > -1) {
      this.#toggleItem(index);
    }
  }

  #toggleItem(index) {
    this.$el.removeClass('noTransition');
    const selIndex = (this._selectedIndex === index) ? -1 : index;
    this.setSelectedIndex(selIndex);
    Analytics.trackLessonTapToReveal();
  }

  #getItemIndexUsingChild(el) {
    for (let i = 0; i < this._items.length; i++) {
      const itemEl = this._items[i].$el[0];
      const hit = (el === itemEl || $.contains(itemEl, el));
      if (hit) {
        return i;
      }
    }
    return -1;
  }
}

// ---

export class TapRevealItem {

  static _TEMPLATE_STRING = `
      <div class="item">
        <div class="unrevealed">
          <div class="unrevealedTitle"><%= title %></div>
        </div>
        <div class="revealed">
          <div class="revealedTitle"><%= revealTitle %></div>
          <div class="revealedCopy"><%= revealSubtitle %></div>
        </div>
        <div class="plus"></div>
      </div>
      `;
  static _template = _.template(TapRevealItem._TEMPLATE_STRING);

  constructor(data) {
    data.title = data.title.toLocaleUpperCase();
    const s = TapRevealItem._template(data);
    this._$el = $(s);
    this._$unrevealedHolder = this._$el.find(".unrevealed");
    this._$revealedHolder = this._$el.find(".revealed");
    this._$revealedCopy = this._$revealedHolder.find(".revealedCopy");
    this._$revealedTitle = this._$revealedHolder.find(".revealedTitle");
    this._$plusIcon = this._$el.find(".plus");
    this._titleOffset = 0;
    this._revealedBgColorClass 
        = PrimerMaster.getLightenedBgColorClassForLessonId(Global.lessonId);
  }

  get $el() {
    return this._$el;
  }

  onFirstShow() {
      const h1 = this._$unrevealedHolder.height();
      const h2 = this._$revealedHolder.height();
      this._titleOffset = (h1 - h2) / 2;
      this.setRevealed(false);
  }

  setRevealed(b) {    
    let offset;
    if (b) {
      FadeUtil.fadeIn(this._$revealedCopy);
      FadeUtil.fadeOut(this._$plusIcon);
      offset = this._titleOffset;
      this._$el.addClass(this._revealedBgColorClass);
      this._$revealedHolder.css('visibility', 'visible'); // must turn this on and off explicitly b/c iOS
    } else {
      FadeUtil.fadeOut(this._$revealedCopy);
      FadeUtil.fadeIn(this._$plusIcon);
      offset = 0;
      this._$el.removeClass(this._revealedBgColorClass);
      this._$revealedHolder.css('visibility', 'hidden');
    }
    this._$unrevealedHolder.css('margin-top', offset + "px");
  }
}
